"use client";

import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Paper, Typography } from "@mui/material";
import { formatPercent } from "@/utils/valueFormatters";
import { chartColors } from "@/theme/chartColors";
import { useTranslation } from "react-i18next";
import { i1 } from "@/i18n";

type RetentionData = {
    [key: string]: number;
};

type RetentionProfileProps = {
    data: {
        your_dealership: RetentionData;
        your_peers: RetentionData;
        all_dealerships: RetentionData;
    };
    title?: string;
    isGroup?: boolean;
};

const RetentionProfileChart: React.FC<RetentionProfileProps> = ({
    data,
    title = i1.t("executiveSummary.sections.retentionProfile"),
    isGroup = false,
}) => {
    const { t } = useTranslation();

    const chartData = {
        // labels: Object.keys(data.your_dealership),
        labels: [
            `6 ${i1.t("time.months", { count: 6 })}`.toLocaleLowerCase(),
            `1 ${i1.t("time.years", { count: 1 })}`.toLocaleLowerCase(),
            `2 ${i1.t("time.years", { count: 2 })}`.toLocaleLowerCase(),
            `3 ${i1.t("time.years", { count: 3 })}`.toLocaleLowerCase(),
            `5 ${i1.t("time.years", { count: 5 })}`.toLocaleLowerCase(),
            `8 ${i1.t("time.years", { count: 8 })}`.toLocaleLowerCase(),
            `10 ${i1.t("time.years", { count: 10 })}`.toLocaleLowerCase(),
        ],
        // labels: ['6 months', '1 year', '2 years', '3 years', '5 years', '8 years', '10 years'],
        series: [
            {
                label: isGroup ? t("common.comparisonGroups.yourGroup") : t("common.comparisonGroups.yourDealership"),
                data: Object.values(data.your_dealership),
            },
            {
                label: t("common.comparisonGroups.yourPeers"),
                data: Object.values(data.your_peers),
            },
            {
                label: t("common.comparisonGroups.allDealerships"),
                data: Object.values(data.all_dealerships),
            },
        ],
    };

    return (
        <Paper
            className="exec-step-6"
            elevation={0}
            sx={{ p: 2, borderRadius: "16px", border: "1px solid #dee2e6" }}
        >
            <Typography
                variant="h6"
                component="h3"
                sx={{ mb: 2, fontWeight: "bold", color: "#002046" }}
            >
                {title}
            </Typography>
            <BarChart
                height={400}
                xAxis={[
                    {
                        scaleType: "band",
                        data: chartData.labels,
                    },
                ]}
                yAxis={[
                    {
                        valueFormatter: (value: number | null) =>
                            formatPercent((value ?? 0) * 100, 0),
                        max: 1,
                    },
                ]}
                series={chartData.series.map((s) => ({
                    data: s.data,
                    label: s.label,
                    valueFormatter: (value: number | null) => formatPercent((value ?? 0) * 100, 1),
                }))}
                slotProps={{
                    legend: {
                        position: { vertical: "top", horizontal: "center" },
                    },
                }}
                colors={chartColors}
            />
        </Paper>
    );
};

export default RetentionProfileChart;
